// A research run's progress on the run status card (components/RunStatus.jsx): its phases, the
// latest thing it found, the evidence cards it has written (each opens to its title and source),
// the questions its research is exploring, and the triggers it has settled on. Everything changes
// by itself as the run streams, so each block is marked as ticking. Props in, plus which card is
// open; the data comes from lib/forecast/deepProgress.ts and hooks/useResearchQuestions.js.
import React, { useState } from "react";
import { C } from "../lib/colors.js";
import { ui } from "../lib/telemetry/instrument.js";
import { Spinner } from "./ui/Primitives.jsx";
import { IconArrowUp, IconCheck, IconExternal } from "./ui/Icons.jsx";
import RunStageRow from "./RunStageRow.jsx";

const MAX_QUESTIONS = 5;
const MAX_TRIGGERS = 8;

/** "2026-10-29" → "Oct 29" (the year when it is not this one). */
function shortDay(day) {
  const d = new Date(`${day}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, color: C.gray, margin: "18px 0 6px", fontWeight: 500 }}>
      {children}
    </div>
  );
}

/** The newest finding, faded in each time it changes. */
function LatestFinding({ text }) {
  return (
    <div
      data-telemetry-ticking=""
      style={{
        marginTop: 16,
        padding: "10px 12px",
        borderRadius: 8,
        background: `color-mix(in srgb, ${C.white} 4%, transparent)`,
        border: `1px solid ${C.border}`,
      }}
    >
      <div style={{ fontSize: 12, color: C.gray, marginBottom: 4, fontWeight: 500 }}>Latest</div>
      <div
        key={text}
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          color: C.white,
          animation: "simpleFadeIn 0.4s ease-out",
        }}
      >
        {text}
      </div>
    </div>
  );
}

/** Open questions first (newest first), then answered ones, at most MAX_QUESTIONS. */
function orderQuestions(questions) {
  const open = questions.filter((q) => !q.done).reverse();
  const done = questions.filter((q) => q.done);
  return [...open, ...done];
}

function Questions({ questions }) {
  const ordered = orderQuestions(questions);
  const shown = ordered.slice(0, MAX_QUESTIONS);
  const more = ordered.length - shown.length;
  return (
    <>
      <SectionLabel>Questions being explored</SectionLabel>
      <ul
        data-telemetry-ticking=""
        style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}
      >
        {shown.map((q) => (
          <li
            key={q.callId}
            style={{
              display: "grid",
              gridTemplateColumns: "20px 1fr",
              columnGap: 10,
              alignItems: "start",
              fontSize: 13,
              lineHeight: 1.45,
              color: q.done ? C.grayL : C.white,
            }}
          >
            <span style={{ display: "flex", justifyContent: "center", paddingTop: 2 }}>
              {q.done ? (
                <IconCheck width={14} height={14} title="Done" style={{ color: C.grayL }} />
              ) : (
                <Spinner size="sm" label="Exploring" />
              )}
            </span>
            <span>{q.text}</span>
          </li>
        ))}
        {more > 0 && (
          <li style={{ fontSize: 12, color: C.gray, paddingLeft: 30 }}>and {more} more</li>
        )}
      </ul>
    </>
  );
}

/** One evidence card's details, under the stack: what it is, where it came from, and a link out. */
function EvidenceDetail({ tile }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${C.border}`,
        background: `color-mix(in srgb, ${C.white} 4%, transparent)`,
        animation: "simpleFadeIn 0.25s ease-out",
      }}
    >
      <div style={{ fontSize: 12, color: C.gray, marginBottom: 4 }}>
        {tile.label}
        {tile.angle ? ` · ${tile.angle}` : ""}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.45, color: C.white }}>{tile.title}</div>
      {tile.source || tile.url ? (
        <div
          style={{
            marginTop: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            fontSize: 12,
            color: C.gray,
          }}
        >
          <span>{tile.source}</span>
          {tile.url ? (
            <a
              {...ui("run-status.evidence-source", tile.label)}
              href={tile.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: C.grayL,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                textDecoration: "none",
              }}
            >
              Open source
              <IconExternal width={12} height={12} />
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The evidence cards written so far, as a growing stack of small cards; one opens at a time. */
function Evidence({ tiles }) {
  const [open, setOpen] = useState(null);
  const openTile = tiles.find((t) => t.n === open) ?? null;
  return (
    <>
      <SectionLabel>Evidence collected</SectionLabel>
      <div data-telemetry-ticking="" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {tiles.map((t) => {
          const selected = t.n === open;
          return (
            <button
              {...ui("run-status.evidence-card", t.label)}
              key={t.n}
              type="button"
              aria-pressed={selected}
              title={t.title}
              onClick={() => setOpen(selected ? null : t.n)}
              className="sy-num"
              style={{
                width: 34,
                height: 42,
                padding: 0,
                borderRadius: 5,
                border: `1px solid ${selected ? C.accent : C.border}`,
                background: selected
                  ? `color-mix(in srgb, ${C.accent} 18%, transparent)`
                  : `color-mix(in srgb, ${C.white} 5%, transparent)`,
                color: selected ? C.white : C.grayL,
                fontSize: 11,
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: 5,
                boxShadow: "0 1px 0 rgba(0,0,0,0.25)",
                animation: "simpleFadeIn 0.35s ease-out",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {openTile ? <EvidenceDetail tile={openTile} /> : null}
      <div style={{ fontSize: 12, color: C.gray, marginTop: 6 }}>
        {openTile
          ? "Full evidence & insights come with the final report."
          : "Open one to see more, full evidence & insights come with the final report."}
      </div>
    </>
  );
}

function Triggers({ points }) {
  const shown = points.slice(0, MAX_TRIGGERS);
  const more = points.length - shown.length;
  return (
    <>
      <SectionLabel>Triggers found</SectionLabel>
      <ul
        data-telemetry-ticking=""
        style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}
      >
        {shown.map((p) => (
          <li
            key={p.id}
            style={{
              display: "grid",
              gridTemplateColumns: "20px 1fr auto",
              columnGap: 10,
              alignItems: "center",
              fontSize: 13,
              color: C.white,
              animation: "simpleFadeIn 0.4s ease-out",
            }}
          >
            <span style={{ display: "flex", justifyContent: "center" }}>
              {p.direction ? (
                <IconArrowUp
                  width={14}
                  height={14}
                  title={
                    p.direction === "up" ? "Would raise the forecast" : "Would lower the forecast"
                  }
                  style={{
                    color: p.direction === "up" ? C.green : C.red,
                    transform: p.direction === "down" ? "rotate(180deg)" : undefined,
                  }}
                />
              ) : null}
            </span>
            <span>{p.title}</span>
            <span className="sy-num" style={{ fontSize: 12, color: C.gray }}>
              {p.date ? shortDay(p.date) : ""}
            </span>
          </li>
        ))}
        {more > 0 && (
          <li style={{ fontSize: 12, color: C.gray, paddingLeft: 30 }}>and {more} more</li>
        )}
      </ul>
    </>
  );
}

/**
 * @param {object} props
 * @param {{key: string, label: string, state: "done"|"current"|"todo", detail: string|null}[]} props.phases
 * @param {string|null} props.finding the latest finding, or null before there is one
 * @param {{n: number, label: string, title: string, source: string, url: string, angle: string|null}[]} props.evidence
 * @param {{callId: string, text: string, done: boolean}[]} props.questions
 * @param {{id: string, title: string, date: string|null, direction: "up"|"down"|null}[]} props.triggers
 */
export default function DeepRunProgress({ phases, finding, evidence, questions, triggers }) {
  return (
    <>
      <ol
        data-telemetry-ticking=""
        style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 2 }}
      >
        {phases.map((p) => (
          <RunStageRow key={p.key} label={p.label} state={p.state} detail={p.detail} />
        ))}
      </ol>
      {finding ? <LatestFinding text={finding} /> : null}
      {evidence.length > 0 ? <Evidence tiles={evidence} /> : null}
      {questions.length > 0 ? <Questions questions={questions} /> : null}
      {triggers.length > 0 ? <Triggers points={triggers} /> : null}
    </>
  );
}
